// src/modules/app-views/services/pages/Init/modals/ServicesParametersModal/tabs/ToolsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';
import ToolFormModal from './ToolFormModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ToolsTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  // Use local state for tools - initialize from store, update locally after save
  const [tools, setTools] = useState(() => {
    const rawTools = appService.getModulePreferences({ category: 'tools' });
    return normalizeTools(rawTools);
  });

  // Filter out removed tools for display in table
  const visibleTools = useMemo(() => tools.filter(t => !t.removed), [tools]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      // Check for duplicates (excluding current item if editing, and excluding removed tools)
      const isDuplicate = tools.some(
        t =>
          t.uid !== editingRecord?.uid &&
          !t.removed &&
          t.name.toLowerCase() === formData.name.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updatedTools;

        if (editingRecord) {
          // Editing existing tool - keep the same uid and preserve removed flag
          updatedTools = tools.map(t =>
            t.uid === editingRecord.uid
              ? {
                  ...t,
                  name: formData.name,
                  type: formData.type,
                  link: formData.link,
                  description: formData.description,
                }
              : t
          );
        } else {
          // Adding new tool - generate new uid
          updatedTools = [
            ...tools,
            {
              uid: generateUidV4(),
              name: formData.name,
              type: formData.type,
              link: formData.link,
              description: formData.description,
            },
          ];
        }

        // Save array directly: tools: [...]
        await appService.updateModulePreferences({
          category: 'tools',
          data: updatedTools,
        });

        // Update local state immediately (don't wait for SSE)
        setTools(updatedTools);

        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
      } catch (error) {
        console.error('[ToolsTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [tools, editingRecord, setLoading]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
  }, []);

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        const moduleId = storedModule?.id || 'services';

        // Check if tool is in use via API
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'tools',
          uid: record.uid,
        });

        let updatedTools;

        if (isInUse) {
          // Soft delete: mark as removed but keep in array
          updatedTools = tools.map(t =>
            t.uid === record.uid ? { ...t, removed: true } : t
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          // Hard delete: remove from array
          updatedTools = tools.filter(t => t.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        // Save array directly: tools: [...]
        await appService.updateModulePreferences({
          category: 'tools',
          data: updatedTools,
        });

        // Update local state immediately (don't wait for SSE)
        setTools(updatedTools);
      } catch (error) {
        console.error('[ToolsTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [tools, setLoading, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('name'),
      dataIndex: 'name',
      width: 160,
      render: text => <span className="font-weight-bold">{text}</span>,
    },
    {
      title: translateX('type'),
      dataIndex: 'type',
      width: 120,
      align: 'center',
      render: type => {
        const color = type === 'physical' ? 'blue' : 'purple';
        const label = type === 'physical' ? translateX('physical_tool') : translateX('software_tool');
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: translateX('link'),
      dataIndex: 'link',
      width: 60,
      align: 'center',
      render: link =>
        link ? (
          <a href={link} target="_blank" rel="noopener noreferrer">
            <LinkOutlined />
          </a>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleTools}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_tool')}
        </Button>
      </Flex>

      <ToolFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
        mousePosition={mousePosition}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ToolsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeTools(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];
  return rawData.map(normalizeTool);
}

function normalizeTool(tool) {
  return {
    uid: tool.uid,
    name: tool.name || '',
    type: tool.type || 'physical',
    link: tool.link || '',
    description: tool.description || '',
    ...(tool.removed && { removed: true }),
  };
}
